import { ArrowForward } from '@material-ui/icons'
import { graphql, useStaticQuery } from 'gatsby'
import { GatsbyImage } from 'gatsby-plugin-image'
import React from 'react'
import Shielded from 'react-shielded'
import styled from 'styled-components'

import { format } from 'date-fns'
import { getLinkTarget } from '../../helpers/url'
import AccessibilitySymbol from '../../images/svg/accessibility_symbol.svg'
import AucklandCouncilLogo from '../../images/svg/akl_council_support.svg'
import Facebook from '../../images/svg/facebook.svg'
import Instagram from '../../images/svg/ig.svg'
import Logo from '../../images/svg/motat_header_new.svg'
import TikTok from '../../images/svg/tiktok.svg'
import TripAdvisor from '../../images/svg/tripadvisor.svg'
import YouTube from '../../images/svg/yt.svg'
import { footerNavigationMock } from '../../mocks/footerNavigation'
import { ExtendedH4 } from '../Typography/Typography'
import Link from '../atoms/link'
import theme from '../atoms/theme'
import Container from '../container'
import FooterNavigationItem from './footer-navigation-item'
import { MotatLogo } from './navigation'

const { breakpoints, colours, animation, icon } = theme

type fixedImage = {
  image: {
    childImageSharp: {
      fixed: {
        base64: string
        height: number
        src: string
        srcSet: string
        width: number
      }
    }
  }
}

const useMotatFunImage = (): fixedImage => {
  return useStaticQuery(
    graphql`
      query motatFunImage {
        image: file(relativePath: { eq: "motat_fun_logo.webp" }) {
          childImageSharp {
            gatsbyImageData(width: 260, layout: FIXED)
          }
        }
      }
    `,
  )
}

const ArrowForwardStyled = styled(ArrowForward)`
  vertical-align: middle;
  margin-left: 1rem;

  &.MuiSvgIcon-root {
    width: ${icon.smallSize};
    height: ${icon.smallSize};
    vertical-align: text-bottom;
  }
`

const FooterDivider = styled.div`
  @media only screen and ${breakpoints.fromNormalScreen} {
    border-bottom: 0.1rem solid ${colours.darkTint1};
  }
`

const FooterStyled = styled.div`
  background-color: ${colours.dark};
  color: ${colours.light};
`

// @ts-ignore
const AucklandCouncilLogoStyled = styled(AucklandCouncilLogo)`
  width: 18rem;
`
// @ts-ignore
const AccessibilitySymbolStyled = styled(AccessibilitySymbol)`
  width: 3rem;

  @media only screen and ${breakpoints.toNormalScreen} {
    margin-top: 1rem;
  }
`

const ThirdsContainer = styled.div`
  display: flex;
  padding-top: 4.2rem;
  padding-bottom: 0;

  @media only screen and ${breakpoints.toNormalScreen} {
    flex-direction: column;
  }

  @media only screen and ${breakpoints.fromNormalScreen} {
    padding-bottom: 3.6rem;
  }
`

const Third = styled.div`
  flex-basis: 100%;
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;

  @media only screen and ${breakpoints.toNormalScreen} {
    border-bottom: 0.1rem solid ${colours.darkTint1};
    padding: 2.8rem 0 2.8rem 0;

    &:first-child {
      padding-top: 0;
    }
  }

  @media only screen and ${breakpoints.fromNormalScreen} {
    border-right: 0.1rem solid ${colours.darkTint1};
    padding: 0 3.6rem;
  }

  a {
    text-decoration: none;
  }

  &:last-child {
    border-right: none;
  }
`

const FooterIcons = styled.div`
  a {
    display: inline-block;
    margin: 0 1rem;
    transition: color ${animation.defaultLength} ease-in-out;

    &:hover {
      color: ${colours.primary};
    }
  }

  a > * {
    width: 3.2rem;
    height: 3.2rem;
  }
`

const SubscribeButton = styled.a`
  background-color: ${colours.darkTint1};
  text-decoration: none;
  width: 100%;
  padding: 1.6rem 3rem 1.6rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 0 0 ${colours.dark};
  transition: box-shadow ${animation.defaultLength} ease-in-out, background-color ${animation.defaultLength} ease-in-out;

  ${ArrowForwardStyled} {
    transform: translate3d(0, 0, 0);
    transition: transform ${animation.defaultLength} ease-in-out;
  }

  &:hover {
    background-color: ${colours.primary};
    box-shadow: 0 0 2rem ${colours.primary};

    ${ArrowForwardStyled} {
      transform: translate3d(1rem, 0, 0);
    }
  }
`

const InfoContainer = styled.ul`
  display: flex;
  padding: 0 0 4rem 0;

  @media only screen and ${breakpoints.toNormalScreen} {
    flex-direction: column;
  }

  @media only screen and ${breakpoints.fromNormalScreen} {
    padding: 3rem 0;
  }
`

const InfoLargeSection = styled.li`
  flex-basis: 33.3%;
  display: block;

  @media only screen and ${breakpoints.toNormalScreen} {
    margin-top: 4rem;
    border-bottom: 0.1rem solid ${colours.darkTint1};
    padding-bottom: 3rem;
  }

  p {
    margin-top: 0;
  }
`

const FooterSection = styled.div`
  @media only screen and ${breakpoints.toNormalScreen} {
    display: block;
  }

  @media only screen and ${breakpoints.fromNormalScreen} {
    border-left: 0.1rem solid ${colours.light};
    padding-left: 1rem;
    margin-left: 1rem;
  }
`

const CopyrightContainer = styled(Container)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: column;

  @media only screen and ${breakpoints.toNormalScreen} {
    padding: 0 2rem 3rem;
    text-align: center;
  }

  @media only screen and ${breakpoints.fromNormalScreen} {
    flex-direction: row;
    padding-top: 2rem;
    padding-bottom: 2rem;
  }

  .copyright {
    display: flex;
    flex-direction: column;

    @media only screen and ${breakpoints.fromNormalScreen} {
      flex-direction: row;
      align-items: center;
    }

    button {
      margin-bottom: 1rem;

      @media only screen and ${breakpoints.fromNormalScreen} {
        margin-bottom: 0;
        margin-right: 1rem;
      }
    }
  }
`

export const StyledH4 = styled(ExtendedH4)`
  @media only screen and ${breakpoints.toNormalScreen} {
    font-size: 1.8rem;
    margin-bottom: 0;

    ${Third} & {
      margin-bottom: 2rem;
    }
  }

  a {
    text-decoration: none;
    transition: color ${animation.defaultLength} ease-in-out;

    &:hover {
      color: ${colours.primary};
    }
  }
`

const Footer = (): JSX.Element => {
  // TODO: Uncomment when the link is working
  // const motatFunImageData = useMotatFunImage()
  // const motatFunImage = motatFunImageData.image.childImageSharp.gatsbyImageData
  const currentYear = format(new Date(), 'yyyy')
  return (
    <FooterStyled data-scroll-section>
      <FooterDivider>
        <Container>
          <ThirdsContainer>
            <Third>
              {/* TODO: Enable this when link working */}
              {/*<Link to="https://motat.fun" aria-label="MOTAT FUN">*/}
              {/*  <GatsbyImage image={motatFunImage} alt="MOTAT FUN" />*/}
              {/*</Link>*/}
              <MotatLogo>
                <Logo />
              </MotatLogo>
            </Third>
            <Third>
              <StyledH4>Find us at</StyledH4>
              <FooterIcons>
                <Link
                  to='https://www.facebook.com/MOTATNZ'
                  aria-label='Facebook'
                >
                  <Facebook />
                </Link>
                <Link
                  to='https://www.youtube.com/user/MOTATNZ'
                  aria-label='YouTube'
                >
                  <YouTube />
                </Link>
                <Link
                  to='https://www.instagram.com/motatnz'
                  aria-label='Instagram'
                >
                  <Instagram />
                </Link>
                <Link to='https://www.tiktok.com/@motat.nz' aria-label='TikTok'>
                  <TikTok />
                </Link>
                <Link
                  to='https://www.tripadvisor.co.nz/Attraction_Review-g1811027-d256915-Reviews-Museum_of_Transport_and_Technology-Auckland_North_Island.html'
                  aria-label='TripAdvisor'
                >
                  <TripAdvisor />
                </Link>
              </FooterIcons>
            </Third>
            <Third>
              <StyledH4>
                Join MOTAT&apos;s mailing list for the latest news and
                promotions.
              </StyledH4>
              <SubscribeButton
                target={getLinkTarget('https://motat.prospect2.com/f/1')}
                href='https://motat.prospect2.com/f/1'
                rel='noreferrer'
              >
                Subscribe
                <ArrowForwardStyled />
              </SubscribeButton>
            </Third>
          </ThirdsContainer>
        </Container>
      </FooterDivider>
      <FooterDivider>
        <Container>
          <InfoContainer>
            {footerNavigationMock?.map((menuItem, navigationItemKey) => {
              return (
                <FooterNavigationItem
                  menuItem={menuItem}
                  // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                  key={navigationItemKey}
                />
              )
            })}
            <InfoLargeSection>
              <StyledH4>Museum of Transport and Technology</StyledH4>
              <p>
                805 Great North Road & 98 Motions Road,
                <br />
                Western Springs, Auckland 1022
              </p>

              <p>
                PO Box 44-114
                <br />
                Pt Chevalier, Auckland 1246
              </p>

              <p>
                Freecall 0800 668 2869
                <br />
                Phone +64 9 815 5800
              </p>
            </InfoLargeSection>
          </InfoContainer>
        </Container>
      </FooterDivider>
      <FooterDivider>
        <CopyrightContainer>
          <div className='copyright'>
            <Shielded />
            <FooterSection>
              &copy; {currentYear} Museum of Transport &amp; Technology
            </FooterSection>
            <FooterSection>
              <Link to='/legal/' aria-label='Terms and Privacy Policy'>
                Terms and Privacy Policy
              </Link>
            </FooterSection>
            <FooterSection>
              MOTAT is a registered charity{' '}
              <Link
                to='https://register.charities.govt.nz/CharitiesRegister/ViewCharity?accountId=bdbe0674-c52b-dd11-8f7f-0015c5f3da29&searchId=a158bbb0-e6dd-41df-a479-8a6de554dd7d'
                aria-label='MOTAT is a registered charity CC30945'
              >
                CC30945
              </Link>
            </FooterSection>
            <FooterSection>
              <Link to='/visit/accessibility/' aria-label='Accessibility'>
                <AccessibilitySymbolStyled />
              </Link>
            </FooterSection>
          </div>
          <AucklandCouncilLogoStyled />
        </CopyrightContainer>
      </FooterDivider>
    </FooterStyled>
  )
}

export default Footer
